/**
 * Karbon360° v2 — CBAM installations.
 *
 * The installation is the unit the CBAM registry recognises: the physical site
 * where the goods are made. This screen owns three things:
 *   1. The 3-step setup wizard (Stepper chrome, EntryStepper shape): pick a
 *      building or go standalone → installation details incl. the O3CI registry
 *      fields → first reporting year (carbon price + verification). EntryStepper
 *      itself is NOT reused — its chrome is welded to the emission-entry flow
 *      (scope badge, scan/manual/CSV modes), none of which applies here — but
 *      the visual grammar (stacked step Cards, DList summaries, k2-g64 with a
 *      context column) is copied so the two wizards read as siblings.
 *   2. The installation cards: identity, reporting years, production processes.
 *   3. The process modal with the per-year attribution editor behind an
 *      "Attribution" disclosure — 100/100 defaults so a single-process factory
 *      never has to think about it.
 *
 * API: the contract's POST only accepts the identity fields, so creation is
 * POST → PUT (registry fields, only when set) → PUT years/:year. Three calls,
 * but each matches the contract exactly rather than hoping the server tolerates
 * extra keys.
 *
 * LANGUAGE: entry forms Albanian, list/analysis surfaces English (house split).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ErrorBoundary from '../../components/ErrorBoundary';
import { Shell } from '../../components/Shell';
import { X } from '../../components/v2/Icons';
import {
  PageHeader, Card, Button, Badge, Callout, Empty, Skeleton, Stepper,
  Field, Input, Select, UnitInput, Modal, DList, Sect, useToast, fmt,
} from '../../components/v2/ui';
import { useT } from '../../i18n';
import CbamGate from './CbamGate';
import {
  CBAM_BASE_YEAR, CBAM_YEARS, SECTORS, sectorLabel, GOODS_CATEGORIES, ROUTES,
  isoFromCountry, NO_CARBON_PRICE, CheckRow, num,
} from './cbam-shared';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const clampPct = (v) => Math.max(0, Math.min(100, Number(v) || 0));

const VER_TONE = { verified: 'ok', verifier_engaged: 'info', unverified: 'out' };
const INSTRUMENTS = ['none', 'ETS', 'carbon tax'];

const EMPTY_WIZ = {
  building_id: '', standalone: false,
  name: '', country: '', address: '', city: '',
  unlocode: '', operator_name: '', operator_email: '',
  cbam_registry_id: '', o3ci_registered: false,
  year: CBAM_BASE_YEAR, carbon_price_due: '0',
  carbon_price_instrument: 'none', verification_status: 'unverified',
};

const EMPTY_ATTR = {
  open: false, dirty: false, year: CBAM_BASE_YEAR,
  direct_share_pct: '100', electricity_share_pct: '100',
  include_stationary: true, include_mobile: false, include_process: true,
  method_note: '',
};

/** The contract shows `shares` as an object; be liberal and accept an array too. */
const shareList = (p) => (Array.isArray(p?.shares) ? p.shares : (p?.shares ? [p.shares] : []));

/** Carbon-price hint, keyed on the (free-text) country of the installation. */
function carbonPriceHint(t, country) {
  const iso = isoFromCountry(country);
  if (iso && NO_CARBON_PRICE.has(iso)) return t('cbam.inst.noCarbonPrice', { country: country || iso });
  return t('cbam.inst.carbonPriceHint');
}

function CbamInstallationsBody() {
  const { t } = useT();
  const { toast } = useToast();

  const [installations, setInstallations] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [wiz, setWiz] = useState(null);          // { step, form, saving, error }
  const [instModal, setInstModal] = useState(null); // { inst, form, saving, error }
  const [yearModal, setYearModal] = useState(null); // { inst, isNew, form, saving, error }
  const [procModal, setProcModal] = useState(null); // { inst, proc|null, form, attr, saving, error }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/cbam/installations`, auth()).then((r) => r.data?.installations || []),
      axios.get(`${API_URL}/api/buildings`, auth())
        .then((r) => (Array.isArray(r.data) ? r.data : r.data?.buildings || []))
        .catch(() => []),
    ])
      .then(([ins, b]) => { if (!alive) return; setInstallations(ins); setBuildings(b); })
      .catch((e) => { if (alive) setError(e.response?.data?.message || 'Could not load the installations.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reloadKey]);
  const reload = () => setReloadKey((k) => k + 1);

  /* ── wizard ──────────────────────────────────────────────────────────── */

  const setWf = (patch) => setWiz((w) => ({ ...w, form: { ...w.form, ...patch } }));

  const pickBuilding = (val) => {
    if (val === '__standalone') {
      setWiz((w) => ({ ...w, form: { ...w.form, building_id: '', standalone: true } }));
      return;
    }
    const b = buildings.find((x) => String(x.id) === String(val));
    setWiz((w) => ({
      ...w,
      form: {
        ...w.form,
        building_id: val, standalone: false,
        country: b?.country || '', address: b?.address || '', city: b?.city || '',
        name: w.form.name || b?.name || '',
      },
    }));
  };

  const saveWizard = async () => {
    const f = wiz.form;
    setWiz((w) => ({ ...w, saving: true, error: '' }));
    try {
      // POST once: the created id sticks on the wizard, so a retry after a
      // failed follow-up PUT updates this installation instead of POSTing a twin.
      let instId = wiz.createdId;
      if (!instId) {
        const body = {
          name: f.name.trim(),
          country: f.country.trim(),
          address: f.address.trim() || undefined,
          city: f.city.trim() || undefined,
          unlocode: f.unlocode.trim() || undefined,
          operator_name: f.operator_name.trim() || undefined,
          operator_email: f.operator_email.trim() || undefined,
        };
        if (f.building_id) body.building_id = Number(f.building_id);
        const r = await axios.post(`${API_URL}/api/cbam/installations`, body, auth());
        instId = r.data?.installation?.id;
        if (instId) setWiz((w) => (w ? { ...w, createdId: instId } : w));
      }
      if (instId && (f.cbam_registry_id.trim() || f.o3ci_registered)) {
        await axios.put(`${API_URL}/api/cbam/installations/${instId}`, {
          cbam_registry_id: f.cbam_registry_id.trim() || null,
          o3ci_registered: f.o3ci_registered,
        }, auth());
      }
      if (instId) {
        await axios.put(`${API_URL}/api/cbam/installations/${instId}/years/${f.year}`, {
          carbon_price_due: Number(f.carbon_price_due) || 0,
          carbon_price_instrument: f.carbon_price_instrument,
          verification_status: f.verification_status,
        }, auth());
      }
      toast(t('cbam.inst.created'));
      setWiz(null);
      reload();
    } catch (e) {
      setWiz((w) => ({ ...w, saving: false, error: e.response?.data?.message || t('cbam.inst.saveFailed') }));
    }
  };

  /* ── installation edit / delete ──────────────────────────────────────── */

  const startEditInst = (inst) => setInstModal({
    inst,
    saving: false,
    error: '',
    form: {
      name: inst.name || '', country: inst.country || '', address: inst.address || '',
      city: inst.city || '', unlocode: inst.unlocode || '',
      operator_name: inst.operator_name || '', operator_email: inst.operator_email || '',
      cbam_registry_id: inst.cbam_registry_id || '', o3ci_registered: !!inst.o3ci_registered,
    },
  });

  const saveInst = async () => {
    const m = instModal;
    if (!m.form.name.trim()) { setInstModal((v) => ({ ...v, error: t('cbam.inst.vName') })); return; }
    setInstModal((v) => ({ ...v, saving: true, error: '' }));
    try {
      await axios.put(`${API_URL}/api/cbam/installations/${m.inst.id}`, {
        ...m.form,
        cbam_registry_id: m.form.cbam_registry_id.trim() || null,
      }, auth());
      toast(t('cbam.inst.updated'));
      setInstModal(null);
      reload();
    } catch (e) {
      setInstModal((v) => ({ ...v, saving: false, error: e.response?.data?.message || t('cbam.inst.saveFailed') }));
    }
  };

  const removeInst = async (inst) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('cbam.inst.confirmDelete', { name: inst.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/cbam/installations/${inst.id}`, auth());
      toast(t('cbam.inst.deleted'));
      reload();
    } catch (e) {
      toast(e.response?.data?.message || t('cbam.inst.saveFailed'), { tone: 'err' });
    }
  };

  /* ── reporting years ─────────────────────────────────────────────────── */

  const startYear = (inst, yearRow) => setYearModal({
    inst,
    isNew: !yearRow,
    saving: false,
    error: '',
    form: yearRow ? {
      year: yearRow.year,
      carbon_price_due: String(num(yearRow.carbon_price_due) ?? 0),
      carbon_price_instrument: yearRow.carbon_price_instrument || 'none',
      carbon_price_coverage_note: yearRow.carbon_price_coverage_note || '',
      verification_status: yearRow.verification_status || 'unverified',
      verifier_name: yearRow.verifier_name || '',
    } : {
      year: CBAM_BASE_YEAR, carbon_price_due: '0', carbon_price_instrument: 'none',
      carbon_price_coverage_note: '', verification_status: 'unverified', verifier_name: '',
    },
  });

  const saveYear = async () => {
    const m = yearModal;
    setYearModal((v) => ({ ...v, saving: true, error: '' }));
    try {
      await axios.put(`${API_URL}/api/cbam/installations/${m.inst.id}/years/${m.form.year}`, {
        carbon_price_due: Number(m.form.carbon_price_due) || 0,
        carbon_price_instrument: m.form.carbon_price_instrument,
        carbon_price_coverage_note: m.form.carbon_price_coverage_note.trim() || null,
        verification_status: m.form.verification_status,
        verifier_name: m.form.verifier_name.trim() || null,
      }, auth());
      toast(t('cbam.inst.yearSaved'));
      setYearModal(null);
      reload();
    } catch (e) {
      setYearModal((v) => ({ ...v, saving: false, error: e.response?.data?.message || t('cbam.inst.saveFailed') }));
    }
  };

  /* ── processes + attribution ─────────────────────────────────────────── */

  const startProc = (inst, proc) => {
    const shares = proc ? shareList(proc) : [];
    const forYear = shares.find((s) => Number(s.year) === CBAM_BASE_YEAR) || shares[0];
    setProcModal({
      inst,
      proc: proc || null,
      saving: false,
      error: '',
      form: proc ? {
        name: proc.name || '', sector: proc.sector || '',
        goods_category: proc.goods_category || '', production_route: proc.production_route || 'K',
      } : { name: '', sector: '', goods_category: '', production_route: 'K' },
      attr: forYear ? {
        ...EMPTY_ATTR,
        year: Number(forYear.year),
        direct_share_pct: String(num(forYear.direct_share_pct) ?? 100),
        electricity_share_pct: String(num(forYear.electricity_share_pct) ?? 100),
        include_stationary: forYear.include_stationary !== false,
        include_mobile: !!forYear.include_mobile,
        include_process: forYear.include_process !== false,
        method_note: forYear.method_note || '',
      } : { ...EMPTY_ATTR },
    });
  };

  const setPf = (patch) => setProcModal((m) => ({ ...m, form: { ...m.form, ...patch } }));
  /** Only value changes mark the shares dirty — opening the disclosure toggles `open` directly on the modal. */
  const setAttr = (patch) => setProcModal((m) => ({ ...m, attr: { ...m.attr, ...patch, dirty: true } }));

  /** Picking another attribution year re-reads that year's stored share row. */
  const pickAttrYear = (year) => setProcModal((m) => {
    const shares = m.proc ? shareList(m.proc) : [];
    const row = shares.find((s) => Number(s.year) === Number(year));
    return {
      ...m,
      attr: row ? {
        ...m.attr,
        year: Number(year), dirty: false,
        direct_share_pct: String(num(row.direct_share_pct) ?? 100),
        electricity_share_pct: String(num(row.electricity_share_pct) ?? 100),
        include_stationary: row.include_stationary !== false,
        include_mobile: !!row.include_mobile,
        include_process: row.include_process !== false,
        method_note: row.method_note || '',
      } : { ...EMPTY_ATTR, open: m.attr.open, year: Number(year) },
    };
  });

  const saveProc = async () => {
    const m = procModal;
    if (!m.form.name.trim() || !m.form.sector || !m.form.goods_category) {
      setProcModal((v) => ({ ...v, error: t('cbam.proc.vRequired') }));
      return;
    }
    setProcModal((v) => ({ ...v, saving: true, error: '' }));
    try {
      const payload = {
        installation_id: m.inst.id,
        name: m.form.name.trim(),
        sector: m.form.sector,
        goods_category: m.form.goods_category,
        production_route: m.form.production_route,
      };
      let pid = m.proc?.id;
      if (pid) await axios.put(`${API_URL}/api/cbam/processes/${pid}`, payload, auth());
      else {
        const r = await axios.post(`${API_URL}/api/cbam/processes`, payload, auth());
        pid = r.data?.process?.id ?? r.data?.id;
      }
      if (m.attr.dirty && pid) {
        await axios.put(`${API_URL}/api/cbam/processes/${pid}/shares/${m.attr.year}`, {
          direct_share_pct: clampPct(m.attr.direct_share_pct),
          electricity_share_pct: clampPct(m.attr.electricity_share_pct),
          include_stationary: m.attr.include_stationary,
          include_mobile: m.attr.include_mobile,
          include_process: m.attr.include_process,
          method_note: m.attr.method_note.trim() || null,
        }, auth());
      }
      toast(t('cbam.proc.saved'));
      setProcModal(null);
      reload();
    } catch (e) {
      setProcModal((v) => ({ ...v, saving: false, error: e.response?.data?.message || t('cbam.inst.saveFailed') }));
    }
  };

  const removeProc = async (proc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('cbam.proc.confirmDelete', { name: proc.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/cbam/processes/${proc.id}`, auth());
      toast(t('cbam.proc.deleted'));
      reload();
    } catch (e) {
      toast(e.response?.data?.message || t('cbam.inst.saveFailed'), { tone: 'err' });
    }
  };

  /* ── render ──────────────────────────────────────────────────────────── */

  if (error) return <Callout tone="err">{error}</Callout>;

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'grid', gap: 10 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={30} />)}</div>
      </Card>
    );
  }

  if (wiz) {
    const f = wiz.form;
    const canStep1 = f.standalone ? !!f.country.trim() : !!f.building_id;
    const canStep2 = !!f.name.trim();
    const building = buildings.find((b) => String(b.id) === String(f.building_id));

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button variant="gh" icon={X.ChevL} onClick={() => setWiz(null)}>{t('cbam.inst.backToList')}</Button>
        </div>

        <Stepper steps={[t('cbam.inst.step1'), t('cbam.inst.step2'), t('cbam.inst.step3')]} current={wiz.step} />

        <div className="k2-g64">
          <div className="k2-gcol">
            {/* ── 1 · location ── */}
            <Card
              title={t('cbam.inst.card1')}
              sub={t('cbam.inst.card1Sub')}
              right={wiz.step > 0 && (
                <Button variant="gh" size="sm" onClick={() => setWiz((w) => ({ ...w, step: 0 }))}>{t('entry.change')}</Button>
              )}
              footer={wiz.step === 0 && (
                <Button variant="mint" style={{ marginLeft: 'auto' }} disabled={!canStep1} onClick={() => setWiz((w) => ({ ...w, step: 1 }))}>
                  {t('entry.continue')}<X.Chev s={13} />
                </Button>
              )}
            >
              {wiz.step === 0 ? (
                <>
                  <Field label={t('cbam.inst.building')} required hint={!f.building_id && !f.standalone ? t('cbam.inst.buildingHint') : undefined}>
                    <Select value={f.standalone ? '__standalone' : f.building_id} onChange={(e) => pickBuilding(e.target.value)}>
                      <option value="">{t('cbam.inst.selectBuilding')}</option>
                      {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      <option value="__standalone">{t('cbam.inst.standalone')}</option>
                    </Select>
                  </Field>

                  <div className="k2-g2">
                    <Field label={t('cbam.inst.country')} required={f.standalone}>
                      <Input value={f.country} onChange={(e) => setWf({ country: e.target.value })} placeholder={t('cbam.inst.countryPh')} />
                    </Field>
                    <Field label={t('cbam.inst.city')}>
                      <Input value={f.city} onChange={(e) => setWf({ city: e.target.value })} />
                    </Field>
                    {f.standalone && (
                      <Field label={t('cbam.inst.address')}>
                        <Input value={f.address} onChange={(e) => setWf({ address: e.target.value })} />
                      </Field>
                    )}
                  </div>
                </>
              ) : (
                <DList rows={[
                  [t('cbam.inst.building'), f.standalone ? t('cbam.inst.standaloneShort') : (building?.name || '—')],
                  [t('cbam.inst.country'), f.country || '—'],
                  f.city ? [t('cbam.inst.city'), f.city] : null,
                ].filter(Boolean)} />
              )}
            </Card>

            {/* ── 2 · identity + registry ── */}
            {wiz.step >= 1 && (
              <Card
                title={t('cbam.inst.card2')}
                sub={t('cbam.inst.card2Sub')}
                right={wiz.step > 1 && (
                  <Button variant="gh" size="sm" onClick={() => setWiz((w) => ({ ...w, step: 1 }))}>{t('entry.change')}</Button>
                )}
                footer={wiz.step === 1 && (
                  <>
                    <Button variant="gh" icon={X.ChevL} onClick={() => setWiz((w) => ({ ...w, step: 0 }))}>{t('entry.back')}</Button>
                    <Button variant="mint" style={{ marginLeft: 'auto' }} disabled={!canStep2} onClick={() => setWiz((w) => ({ ...w, step: 2 }))}>
                      {t('entry.continue')}<X.Chev s={13} />
                    </Button>
                  </>
                )}
              >
                {wiz.step === 1 ? (
                  <>
                    <div className="k2-g2">
                      <Field label={t('cbam.inst.name')} required>
                        <Input value={f.name} onChange={(e) => setWf({ name: e.target.value })} autoFocus />
                      </Field>
                      <Field label={t('cbam.inst.unlocode')} optional hint={t('cbam.inst.unlocodeHint')}>
                        <Input value={f.unlocode} onChange={(e) => setWf({ unlocode: e.target.value.toUpperCase() })} placeholder="BA SJJ" maxLength={10} />
                      </Field>
                      <Field label={t('cbam.inst.operatorName')}>
                        <Input value={f.operator_name} onChange={(e) => setWf({ operator_name: e.target.value })} />
                      </Field>
                      <Field label={t('cbam.inst.operatorEmail')}>
                        <Input type="email" value={f.operator_email} onChange={(e) => setWf({ operator_email: e.target.value })} />
                      </Field>
                      <Field label={t('cbam.inst.registryId')} optional hint={t('cbam.inst.registryIdHint')}>
                        <Input value={f.cbam_registry_id} onChange={(e) => setWf({ cbam_registry_id: e.target.value })} />
                      </Field>
                    </div>
                    <CheckRow
                      checked={f.o3ci_registered}
                      onChange={(v) => setWf({ o3ci_registered: v })}
                      label={t('cbam.inst.o3ci')}
                      style={{ marginBottom: 12 }}
                    />
                    <Callout tone="info">{t('cbam.inst.o3ciCallout')}</Callout>
                  </>
                ) : (
                  <DList rows={[
                    [t('cbam.inst.name'), f.name],
                    [t('cbam.inst.unlocode'), f.unlocode || '—'],
                    [t('cbam.inst.operatorName'), f.operator_name || '—'],
                    [t('cbam.inst.registryId'), f.cbam_registry_id || '—'],
                    [t('cbam.inst.o3ci'), f.o3ci_registered ? '✓' : '—'],
                  ]} />
                )}
              </Card>
            )}

            {/* ── 3 · reporting year ── */}
            {wiz.step === 2 && (
              <Card
                title={t('cbam.inst.card3')}
                sub={t('cbam.inst.card3Sub')}
                footer={
                  <>
                    <Button variant="gh" icon={X.ChevL} onClick={() => setWiz((w) => ({ ...w, step: 1 }))}>{t('entry.back')}</Button>
                    <Button variant="mint" icon={X.Check} style={{ marginLeft: 'auto' }} disabled={wiz.saving} onClick={saveWizard}>
                      {wiz.saving ? t('cbam.inst.creating') : t('cbam.inst.create')}
                    </Button>
                  </>
                }
              >
                <div className="k2-g2">
                  <Field label={t('cbam.inst.year')}>
                    <Select value={f.year} onChange={(e) => setWf({ year: Number(e.target.value) })}>
                      {CBAM_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </Select>
                  </Field>
                  <Field label={t('cbam.inst.carbonPrice')} hint={carbonPriceHint(t, f.country)}>
                    <UnitInput unit="€/tCO₂e" type="number" min="0" step="0.01" value={f.carbon_price_due} onChange={(e) => setWf({ carbon_price_due: e.target.value })} />
                  </Field>
                  <Field label={t('cbam.inst.instrument')}>
                    <Select value={f.carbon_price_instrument} onChange={(e) => setWf({ carbon_price_instrument: e.target.value })}>
                      {INSTRUMENTS.map((i) => <option key={i} value={i}>{i}</option>)}
                    </Select>
                  </Field>
                  <Field label={t('cbam.inst.verification')}>
                    <Select value={f.verification_status} onChange={(e) => setWf({ verification_status: e.target.value })}>
                      {['unverified', 'verifier_engaged', 'verified'].map((v) => (
                        <option key={v} value={v}>{t(`cbam.inst.ver.${v}`)}</option>
                      ))}
                    </Select>
                  </Field>
                </div>
                {wiz.error && <div style={{ marginTop: 12 }}><Callout tone="err">{wiz.error}</Callout></div>}
              </Card>
            )}
          </div>

          {/* ── context column ── */}
          <div className="k2-gcol">
            <Card title={t('cbam.inst.contextTitle')}>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--n600)' }}>{t('cbam.inst.contextBody')}</p>
            </Card>
            <Callout tone="info">{t('cbam.gate.info')}</Callout>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button icon={X.Plus} variant="mint" onClick={() => setWiz({ step: 0, form: { ...EMPTY_WIZ }, saving: false, error: '' })}>
          {t('cbam.inst.new')}
        </Button>
      </div>

      {installations.length === 0 ? (
        <Card>
          <Empty
            icon={X.Factory}
            title={t('cbam.inst.empty')}
            body={t('cbam.inst.emptyBody')}
            action={
              <Button variant="mint" icon={X.Plus} onClick={() => setWiz({ step: 0, form: { ...EMPTY_WIZ }, saving: false, error: '' })}>
                {t('cbam.inst.new')}
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="k2-gcol">
          {installations.map((inst) => {
            const years = Array.isArray(inst.years) ? inst.years : [];
            const procs = Array.isArray(inst.processes) ? inst.processes : [];
            return (
              <Card
                key={inst.id}
                title={inst.name}
                sub={[inst.city, inst.country].filter(Boolean).join(', ') || '—'}
                right={
                  <>
                    {inst.o3ci_registered
                      ? <Badge tone="ok" dot>O3CI</Badge>
                      : <Badge tone="out">Not in O3CI</Badge>}
                    <Button size="sm" variant="gh" iconOnly icon={X.Gear} aria-label={`Edit ${inst.name}`} onClick={() => startEditInst(inst)} />
                    <Button size="sm" variant="gh" iconOnly icon={X.Close} aria-label={`Delete ${inst.name}`} onClick={() => removeInst(inst)} />
                  </>
                }
              >
                <DList rows={[
                  ['UN/LOCODE', inst.unlocode || '—'],
                  ['Operator', [inst.operator_name, inst.operator_email].filter(Boolean).join(' · ') || '—'],
                  ['CBAM registry ID', inst.cbam_registry_id || '—'],
                ]} />

                <Sect title="Reporting years">
                  {years.length === 0 && (
                    <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--n500)' }}>No reporting year set up yet.</p>
                  )}
                  {years.map((y) => (
                    <div key={y.year} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--n100)' }}>
                      <span className="k2-num" style={{ fontWeight: 600 }}>{y.year}</span>
                      <span style={{ fontSize: 12.5, color: 'var(--n600)' }}>
                        Carbon price {fmt(num(y.carbon_price_due) ?? 0, 2)} €/tCO₂e
                        {y.carbon_price_instrument && y.carbon_price_instrument !== 'none' ? ` · ${y.carbon_price_instrument}` : ''}
                      </span>
                      <span style={{ marginLeft: 'auto' }}>
                        <Badge tone={VER_TONE[y.verification_status] || 'out'}>{t(`cbam.inst.ver.${y.verification_status || 'unverified'}`)}</Badge>
                      </span>
                      <Button size="sm" variant="gh" iconOnly icon={X.Gear} aria-label={`Edit year ${y.year}`} onClick={() => startYear(inst, y)} />
                    </div>
                  ))}
                  <div style={{ marginTop: 10 }}>
                    <Button size="sm" icon={X.Plus} onClick={() => startYear(inst, null)}>{t('cbam.inst.addYear')}</Button>
                  </div>
                </Sect>

                <Sect title="Production processes">
                  {procs.length === 0 && (
                    <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--n500)' }}>
                      No processes yet — every exported good must belong to one.
                    </p>
                  )}
                  {procs.map((p) => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--n100)' }}>
                      <span style={{ minWidth: 0 }}>
                        <span className="k2-nm" style={{ display: 'block', fontWeight: 500, color: 'var(--n900)' }}>{p.name}</span>
                        <span className="k2-sub2" style={{ display: 'block', fontSize: 11.5, color: 'var(--n500)', marginTop: 1 }}>{p.goods_category || '—'} · route {p.production_route || '—'}</span>
                      </span>
                      <span style={{ marginLeft: 'auto' }}><Badge tone="dark">{sectorLabel(p.sector)}</Badge></span>
                      <Button size="sm" variant="gh" iconOnly icon={X.Gear} aria-label={`Edit ${p.name}`} onClick={() => startProc(inst, p)} />
                      <Button size="sm" variant="gh" iconOnly icon={X.Close} aria-label={`Delete ${p.name}`} onClick={() => removeProc(p)} />
                    </div>
                  ))}
                  <div style={{ marginTop: 10 }}>
                    <Button size="sm" icon={X.Plus} onClick={() => startProc(inst, null)}>{t('cbam.proc.add')}</Button>
                  </div>
                </Sect>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── edit installation ── */}
      {instModal && (
        <Modal
          title={t('cbam.inst.edit')}
          wide
          onClose={() => setInstModal(null)}
          footer={
            <>
              <Button onClick={() => setInstModal(null)}>{t('cbam.form.cancel')}</Button>
              <Button variant="mint" icon={X.Check} disabled={instModal.saving} onClick={saveInst}>
                {instModal.saving ? t('entry.posting') : t('cbam.inst.saveCta')}
              </Button>
            </>
          }
        >
          {instModal.error && <div style={{ marginBottom: 14 }}><Callout tone="err">{instModal.error}</Callout></div>}
          <div className="k2-g2">
            <Field label={t('cbam.inst.name')} required>
              <Input value={instModal.form.name} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, name: e.target.value } }))} />
            </Field>
            <Field label={t('cbam.inst.unlocode')} optional hint={t('cbam.inst.unlocodeHint')}>
              <Input value={instModal.form.unlocode} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, unlocode: e.target.value.toUpperCase() } }))} maxLength={10} />
            </Field>
            <Field label={t('cbam.inst.country')}>
              <Input value={instModal.form.country} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, country: e.target.value } }))} />
            </Field>
            <Field label={t('cbam.inst.city')}>
              <Input value={instModal.form.city} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, city: e.target.value } }))} />
            </Field>
            <Field label={t('cbam.inst.address')}>
              <Input value={instModal.form.address} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, address: e.target.value } }))} />
            </Field>
            <Field label={t('cbam.inst.operatorName')}>
              <Input value={instModal.form.operator_name} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, operator_name: e.target.value } }))} />
            </Field>
            <Field label={t('cbam.inst.operatorEmail')}>
              <Input type="email" value={instModal.form.operator_email} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, operator_email: e.target.value } }))} />
            </Field>
            <Field label={t('cbam.inst.registryId')} optional hint={t('cbam.inst.registryIdHint')}>
              <Input value={instModal.form.cbam_registry_id} onChange={(e) => setInstModal((m) => ({ ...m, form: { ...m.form, cbam_registry_id: e.target.value } }))} />
            </Field>
          </div>
          <CheckRow
            checked={instModal.form.o3ci_registered}
            onChange={(v) => setInstModal((m) => ({ ...m, form: { ...m.form, o3ci_registered: v } }))}
            label={t('cbam.inst.o3ci')}
          />
        </Modal>
      )}

      {/* ── reporting year ── */}
      {yearModal && (
        <Modal
          title={yearModal.isNew ? t('cbam.inst.addYear') : t('cbam.inst.editYear', { year: yearModal.form.year })}
          sub={yearModal.inst.name}
          onClose={() => setYearModal(null)}
          footer={
            <>
              <Button onClick={() => setYearModal(null)}>{t('cbam.form.cancel')}</Button>
              <Button variant="mint" icon={X.Check} disabled={yearModal.saving} onClick={saveYear}>
                {yearModal.saving ? t('entry.posting') : t('cbam.inst.yearSaveCta')}
              </Button>
            </>
          }
        >
          {yearModal.error && <div style={{ marginBottom: 14 }}><Callout tone="err">{yearModal.error}</Callout></div>}
          <div className="k2-g2">
            <Field label={t('cbam.inst.year')}>
              {/* The year is the row key — changing it on an existing row would create a second year, so lock it. */}
              <Select
                value={yearModal.form.year}
                disabled={!yearModal.isNew}
                onChange={(e) => setYearModal((m) => ({ ...m, form: { ...m.form, year: Number(e.target.value) } }))}
              >
                {CBAM_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </Select>
            </Field>
            <Field label={t('cbam.inst.carbonPrice')} hint={carbonPriceHint(t, yearModal.inst.country)}>
              <UnitInput unit="€/tCO₂e" type="number" min="0" step="0.01" value={yearModal.form.carbon_price_due} onChange={(e) => setYearModal((m) => ({ ...m, form: { ...m.form, carbon_price_due: e.target.value } }))} />
            </Field>
            <Field label={t('cbam.inst.instrument')}>
              <Select value={yearModal.form.carbon_price_instrument} onChange={(e) => setYearModal((m) => ({ ...m, form: { ...m.form, carbon_price_instrument: e.target.value } }))}>
                {INSTRUMENTS.map((i) => <option key={i} value={i}>{i}</option>)}
              </Select>
            </Field>
            <Field label={t('cbam.inst.verification')}>
              <Select value={yearModal.form.verification_status} onChange={(e) => setYearModal((m) => ({ ...m, form: { ...m.form, verification_status: e.target.value } }))}>
                {['unverified', 'verifier_engaged', 'verified'].map((v) => (
                  <option key={v} value={v}>{t(`cbam.inst.ver.${v}`)}</option>
                ))}
              </Select>
            </Field>
            {yearModal.form.verification_status !== 'unverified' && (
              <Field label={t('cbam.inst.verifierName')}>
                <Input value={yearModal.form.verifier_name} onChange={(e) => setYearModal((m) => ({ ...m, form: { ...m.form, verifier_name: e.target.value } }))} />
              </Field>
            )}
            <Field label={t('cbam.inst.coverageNote')} optional>
              <Input value={yearModal.form.carbon_price_coverage_note} onChange={(e) => setYearModal((m) => ({ ...m, form: { ...m.form, carbon_price_coverage_note: e.target.value } }))} />
            </Field>
          </div>
        </Modal>
      )}

      {/* ── process + attribution ── */}
      {procModal && (
        <Modal
          title={procModal.proc ? t('cbam.proc.edit') : t('cbam.proc.new')}
          sub={procModal.inst.name}
          wide
          onClose={() => setProcModal(null)}
          footer={
            <>
              <Button onClick={() => setProcModal(null)}>{t('cbam.form.cancel')}</Button>
              <Button variant="mint" icon={X.Check} disabled={procModal.saving} onClick={saveProc}>
                {procModal.saving ? t('entry.posting') : t('cbam.proc.saveCta')}
              </Button>
            </>
          }
        >
          {procModal.error && <div style={{ marginBottom: 14 }}><Callout tone="err">{procModal.error}</Callout></div>}
          <div className="k2-g2">
            <Field label={t('cbam.proc.name')} required>
              <Input value={procModal.form.name} onChange={(e) => setPf({ name: e.target.value })} placeholder={t('cbam.proc.namePh')} autoFocus />
            </Field>
            <Field label={t('cbam.proc.sector')} required>
              <Select
                value={procModal.form.sector}
                onChange={(e) => setPf({ sector: e.target.value, goods_category: (GOODS_CATEGORIES[e.target.value] || [])[0] || '' })}
              >
                <option value="">—</option>
                {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </Field>
            <Field label={t('cbam.proc.category')} required>
              <Select value={procModal.form.goods_category} onChange={(e) => setPf({ goods_category: e.target.value })} disabled={!procModal.form.sector}>
                <option value="">—</option>
                {(GOODS_CATEGORIES[procModal.form.sector] || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label={t('cbam.proc.route')} hint={t('cbam.proc.routeHint')}>
              <Select value={procModal.form.production_route} onChange={(e) => setPf({ production_route: e.target.value })}>
                {ROUTES.map((r) => <option key={r} value={r}>{t(`cbam.route.${r}`)}</option>)}
              </Select>
            </Field>
          </div>

          {/* Attribution lives behind a disclosure: a single-process factory keeps the 100/100 default and never opens it. */}
          <div style={{ marginTop: 6, borderTop: '1px solid var(--n100)', paddingTop: 12 }}>
            <Button
              variant="gh"
              icon={procModal.attr.open ? X.ChevU : X.ChevD}
              onClick={() => setProcModal((m) => ({ ...m, attr: { ...m.attr, open: !m.attr.open } }))}
            >
              {t('cbam.proc.attr')}
              <span style={{ fontWeight: 400, color: 'var(--n500)' }}>
                · {procModal.attr.direct_share_pct}% / {procModal.attr.electricity_share_pct}%
              </span>
            </Button>

            {procModal.attr.open && (
              <div style={{ marginTop: 12 }}>
                <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--n500)' }}>{t('cbam.proc.attrSub')}</p>
                {procModal.form.goods_category === 'Aluminium products' && (
                  <div style={{ marginBottom: 12 }}><Callout tone="warn">{t('cbam.proc.boundaryAl')}</Callout></div>
                )}
                <div className="k2-g2">
                  <Field label={t('cbam.proc.attrYear')}>
                    <Select value={procModal.attr.year} onChange={(e) => pickAttrYear(e.target.value)}>
                      {CBAM_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </Select>
                  </Field>
                  <div />
                  <Field label={t('cbam.proc.directShare')}>
                    <UnitInput unit="%" type="number" min="0" max="100" step="0.1" value={procModal.attr.direct_share_pct} onChange={(e) => setAttr({ direct_share_pct: e.target.value })} />
                  </Field>
                  <Field label={t('cbam.proc.elecShare')}>
                    <UnitInput unit="%" type="number" min="0" max="100" step="0.1" value={procModal.attr.electricity_share_pct} onChange={(e) => setAttr({ electricity_share_pct: e.target.value })} />
                  </Field>
                </div>
                <div style={{ display: 'grid', gap: 8, margin: '4px 0 12px' }}>
                  <CheckRow checked={procModal.attr.include_stationary} onChange={(v) => setAttr({ include_stationary: v })} label={t('cbam.proc.inclStationary')} />
                  <CheckRow checked={procModal.attr.include_mobile} onChange={(v) => setAttr({ include_mobile: v })} label={t('cbam.proc.inclMobile')} hint={t('cbam.proc.inclMobileHint')} />
                  <CheckRow checked={procModal.attr.include_process} onChange={(v) => setAttr({ include_process: v })} label={t('cbam.proc.inclProcess')} />
                </div>
                <Field label={t('cbam.proc.methodNote')} optional>
                  <Input value={procModal.attr.method_note} onChange={(e) => setAttr({ method_note: e.target.value })} placeholder={t('cbam.proc.methodNotePh')} />
                </Field>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

function CbamInstallationsScreen() {
  const { t } = useT();
  return (
    <div className="k2-wrap">
      <PageHeader title={t('cbam.installations.title')} desc={t('cbam.installations.desc')} />
      <CbamGate>
        <CbamInstallationsBody />
      </CbamGate>
    </div>
  );
}

export default function CbamInstallationsPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <CbamInstallationsScreen />
      </Shell>
    </ErrorBoundary>
  );
}
